import React from 'react';
import styled, { css } from 'styled-components';
import { filterXSS } from 'xss';
import { useTranslation } from '@/app/components/translations/Translation';

import customCss from '@/app/components/hotel-detail/css/customCss';
import { TextBase } from '@/app/components/hotel-detail/common/text';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { Icon } from '@/app/common/icons/Icon';
import { useUrlSearchParams } from './UrlSearchParamsProvider';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  @media ${customCss.breakpoints.sm} {
    padding: 24px;
  }

  ul {
    display: flex;
    flex-direction: column;
    padding: 0;
    list-style: none;
    margin: 0;
  }

  li {
    padding: 8px 0;
    border-bottom: 1px dashed ${(p) => p.theme.commons.colors.gray100};
    display: flex;
    justify-content: space-between;

    :nth-child(3) {
      border-bottom: 1px solid ${(p) => p.theme.commons.colors.gray100};
    }

    :last-child {
      border-bottom: none;
    }
  }
`;

const Voucher = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  background-color: ${(p) => p.theme.commons.colors.gray50};
  border-radius: 8px;
  padding: 12px 16px;
`;

export const BackToTravelMessage = ({
  packAmount,
  discountAmount,
  voucherAmount,
  priceType,
  finalPrice,
  strikeThroughPrice,
  ...rest
}: MealPlanBackToTravel & React.HTMLAttributes<HTMLDivElement>) => {
  const { t } = useTranslation();
  const { isTrain } = useUrlSearchParams();
  const { localization } = usePageWrapperData();

  return (
    <Container {...rest}>
      <TextBase>
        {t(
          isTrain
            ? 'vacation.select.package_savings.back_to_travel.price_details.tooltip_heading.train'
            : 'vacation.select.package_savings.back_to_travel.price_details.tooltip_heading'
        )}
      </TextBase>
      <ul>
        <li>
          <TextBase>
            {t(
              isTrain
                ? 'vacation.select.package_savings.back_to_travel.price_details.basic_fare.train'
                : 'vacation.select.package_savings.back_to_travel.price_details.basic_fare'
            )}
          </TextBase>
          <TextBase>
            {`${formatPrice({ price: strikeThroughPrice, localization })} ${priceType === 'PER_PERSON' && t('vacation.oss.cards.pp')}`}
          </TextBase>
        </li>
        <li>
          <TextBase>{t('vacation.select.package_savings.back_to_travel.price_details.pack')}</TextBase>
          <TextBase>
            {`${formatPrice({ price: packAmount, localization })} ${priceType === 'PER_PERSON' && t('vacation.oss.cards.pp')}`}
          </TextBase>
        </li>
        <li>
          <TextBase>{t('vacation.select.package_savings.back_to_travel.price_details.discount')}</TextBase>
          <TextBase>
            {`- ${formatPrice({ price: discountAmount, localization })} ${priceType === 'PER_PERSON' && t('vacation.oss.cards.pp')}`}
          </TextBase>
        </li>
        <li
          css={css`
            font-weight: ${(p) => p.theme.commons.font.weights.bold};
          `}
        >
          <TextBase>{t('vacation.select.package_savings.back_to_travel.price_details.pay_now')}</TextBase>
          <TextBase>
            {`${formatPrice({ price: finalPrice, localization })} ${priceType === 'PER_PERSON' && t('vacation.oss.cards.pp')}`}
          </TextBase>
        </li>
      </ul>
      <Voucher>
        <Icon category="Financial" name="icon-gift-card" size={24} color="iconPrimary" alt="voucher" />
        <TextBase
          dangerouslySetInnerHTML={{
            __html: filterXSS(
              t('vacation.oss.package_savings.back_to_travel.price_details.next_trip', {
                amount: formatPrice({ price: voucherAmount, localization }),
              })?.replace(/\*(.*?)\*/g, '<b>$1</b>')
            ),
          }}
        />
      </Voucher>
      <TextBase>{t('vacation.select.package_savings.back_to_travel.price_details.tooltip_footer')}</TextBase>
    </Container>
  );
};
